import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Box, Typography, Button, TextField, MenuItem, TableBody, TableCell, TableHead, TableRow, Modal, Select, FormControl, InputLabel, Checkbox, CircularProgress, IconButton, TableSortLabel, Chip, Fade, Divider, Alert, InputAdornment, useMediaQuery, useTheme, Card, CardContent, Collapse, Stack, Grid2, Autocomplete, Link, Popover } from '@mui/material';
import Tooltip from '../Shared/CopyableTooltip';
import { useUI } from "../Context/UIContext";
import TableLoadingOverlay from "../Shared/TableLoadingOverlay";
import AdminPageHeader, { adminPageShellSx } from "../Shared/AdminPageHeader";
import ScrollTopFab from "../Shared/ScrollTopFab";
import ActionIconButton from "../Shared/ActionIconButton";
import PropTypes from "prop-types";
import SearchIcon from '@mui/icons-material/Search';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CloudSyncIcon from '@mui/icons-material/CloudSync';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ImageNotSupportedIcon from '@mui/icons-material/ImageNotSupported';
import BarChartIcon from '@mui/icons-material/BarChart';
import TuneIcon from '@mui/icons-material/Tune';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { searchProducts, associateProducts, getProductStats, getAllVendors, getAllCategories, updateProduct } from "../../services/catalogServices";
import AdminMergedTable from "../Shared/AdminMergedTable";
import AdminTablePagination from "../Shared/AdminTablePagination";
import useStickyTableHeader from "../../utils/useStickyTableHeader";
import { mergedTableHeadSx, mergedTableRowSx } from "../../utils/adminTableScrollSx";
import { usePermissions } from "../Context/permissionsProvider";
import ConfirmationModal from "../Shared/confirmationModal";
import CategoryTreeFilter from "../Shared/CategoryTreeFilter";

const StatusBadge = ({ quantity }) => {
  const isAvailable = quantity > 0;
  return (
    <Chip
      icon={isAvailable ? <CheckCircleOutlineIcon /> : <ErrorOutlineIcon />}
      label={isAvailable ? `Disp. (${quantity})` : "Esaurito"}
      variant="outlined"
      size="small"
      sx={{
        fontWeight: 600,
        borderColor: isAvailable ? "#4caf50" : "#f44336",
        color: isAvailable ? "#2e7d32" : "#d32f2f",
        backgroundColor: isAvailable ? "#f1f8e9" : "#ffebee",
        "& .MuiChip-icon": { color: "inherit" }
      }}
    />
  );
};

StatusBadge.propTypes = {
  quantity: PropTypes.number.isRequired
};

// Un prodotto è "attivo" solo se non è né disattivato né eliminato.
const isProdottoAttivo = (row) => (row.isDisattivato ?? 0) === 0 && (row.isDeleted ?? 0) === 0;

// Chip per lo stato del prodotto (attivo/non attivo).
// Se viene passato `onClick` diventa cliccabile per cambiare lo stato.
const ProductStateBadge = ({ attivo, onClick }) => {
  const chip = (
    <Chip
      label={attivo ? "Attivo" : "Non Attivo"}
      size="small"
      onClick={onClick}
      sx={{
        fontWeight: 700,
        fontSize: "0.7rem",
        backgroundColor: attivo ? "#ecfdf5" : "#fef2f2",
        color: attivo ? "#10b981" : "#ef4444",
        border: `1px solid ${attivo ? "#d1fae5" : "#fee2e2"}`,
        borderRadius: "8px",
        cursor: onClick ? "pointer" : "default",
        transition: "all 0.2s",
        "&:hover": onClick ? { opacity: 0.8, transform: "scale(1.05)" } : undefined
      }}
    />
  );
  if (!onClick) return chip;
  return (
    <Tooltip title="Clicca per cambiare stato" arrow copyable={false}>
      {chip}
    </Tooltip>
  );
};

ProductStateBadge.propTypes = {
  attivo: PropTypes.bool.isRequired,
  onClick: PropTypes.func
};

// Chip di sola lettura per la categorizzazione (certificato/non certificato).
const CategorizationBadge = ({ certificato }) => (
  <Chip
    label={certificato ? "Certificato" : "Non cert."}
    size="small"
    sx={{
      fontWeight: 700,
      fontSize: "0.7rem",
      backgroundColor: certificato ? "#ecfdf5" : "#f1f5f9",
      color: certificato ? "#10b981" : "#64748b",
      border: `1px solid ${certificato ? "#d1fae5" : "#e2e8f0"}`,
      borderRadius: "8px"
    }}
  />
);

CategorizationBadge.propTypes = {
  certificato: PropTypes.bool
};

const LogoCarousel = ({ logos, onImageClick }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [error, setError] = useState(false);

  if (!logos || logos.length === 0) {
    return (
      <Typography variant="body2" sx={{ color: "#94a3b8", textAlign: "center" }}>
        -
      </Typography>
    );
  }

  const handleNext = (e) => {
    e.stopPropagation();
    if (currentIndex < logos.length - 1) {
      setCurrentIndex((prev) => prev + 1);
      setError(false);
    }
  };

  const handlePrev = (e) => {
    e.stopPropagation();
    if (currentIndex > 0) {
      setCurrentIndex((prev) => prev - 1);
      setError(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 'max-content' }}>
      <IconButton
        size="small"
        onClick={handlePrev}
        disabled={currentIndex === 0 || logos.length <= 1}
        sx={{ p: 0, color: "#64748b" }}
      >
        <ChevronLeftIcon fontSize="small" />
      </IconButton>

      <Box sx={{ position: 'relative', width: 42, height: 42, mx: 0.5 }}>
        {error ? (
          <Box sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: "#f8fafc",
            borderRadius: "6px",
            border: "1px dashed #cbd5e1"
          }}>
            <ImageNotSupportedIcon sx={{ fontSize: 18, color: "#94a3b8" }} />
          </Box>
        ) : (
          <>
            <Box
              component="img"
              src={logos[currentIndex]}
              alt="Logo prodotto"
              onError={() => setError(true)}
              onClick={() => onImageClick?.(logos, currentIndex)}
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                borderRadius: '6px',
                border: '1px solid #f1f5f9',
                backgroundColor: "white",
                cursor: 'pointer',
                '&:hover': { opacity: 0.8, transform: 'scale(1.05)' },
                transition: 'all 0.2s'
              }}
            />
            {logos.length > 1 && (
              <Box sx={{
                position: 'absolute',
                bottom: -2,
                right: -2,
                backgroundColor: 'rgba(11, 61, 135, 0.85)',
                color: 'white',
                fontSize: '0.65rem',
                fontWeight: 700,
                px: 0.5,
                borderRadius: '4px',
                lineHeight: 1,
                padding: '2px 4px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
              }}>
                {currentIndex + 1}
              </Box>
            )}
          </>
        )}
      </Box>

      <IconButton
        size="small"
        onClick={handleNext}
        disabled={currentIndex === logos.length - 1 || logos.length <= 1}
        sx={{ p: 0, color: "#64748b" }}
      >
        <ChevronRightIcon fontSize="small" />
      </IconButton>
    </Box>
  );
};

LogoCarousel.propTypes = {
  logos: PropTypes.arrayOf(PropTypes.string),
  onImageClick: PropTypes.func.isRequired
};

const ImageLightbox = ({ open, onClose, images, currentIndex, onPrev, onNext }) => {
  if (!images || images.length === 0) return null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      closeAfterTransition
      slots={{ backdrop: Box }}
      slotProps={{
        backdrop: {
          sx: {
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            backdropFilter: 'blur(8px)',
            zIndex: -1
          }
        }
      }}
    >
      <Fade in={open}>
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '90vw', md: 'auto' },
          maxWidth: '90vw',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          outline: 'none'
        }}>
          <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {images.length > 1 && (
              <IconButton
                onClick={(e) => { e.stopPropagation(); onPrev(); }}
                sx={{
                  position: 'absolute',
                  left: { xs: -40, md: -60 },
                  color: 'white',
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.2)' }
                }}
              >
                <ChevronLeftIcon fontSize="large" />
              </IconButton>
            )}

            <Box
              component="img"
              src={images[currentIndex]}
              alt="Zoom prodotto"
              sx={{
                maxWidth: '100%',
                maxHeight: '75vh',
                objectFit: 'contain',
                borderRadius: '12px',
                boxShadow: '0 20px 40px rgba(0,0,0,0.4)',
                backgroundColor: 'white',
                border: '4px solid white'
              }}
            />

            {images.length > 1 && (
              <IconButton
                onClick={(e) => { e.stopPropagation(); onNext(); }}
                sx={{
                  position: 'absolute',
                  right: { xs: -40, md: -60 },
                  color: 'white',
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.2)' }
                }}
              >
                <ChevronRightIcon fontSize="large" />
              </IconButton>
            )}
          </Box>

          <Box sx={{
            mt: 3,
            p: 1.5,
            px: 3,
            backgroundColor: 'rgba(255,255,255,0.9)',
            borderRadius: '12px',
            textAlign: 'center',
            maxWidth: '100%'
          }}>
            <Link
              href={images[currentIndex]}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                color: '#0b3d87',
                fontWeight: 700,
                fontSize: '0.85rem',
                textDecoration: 'none',
                wordBreak: 'break-all',
                '&:hover': { textDecoration: 'underline' }
              }}
            >
              Visualizza immagine originale
            </Link>
            {images.length > 1 && (
              <Typography variant="caption" display="block" sx={{ mt: 0.5, color: '#64748b', fontWeight: 600 }}>
                Immagine {currentIndex + 1} di {images.length}
              </Typography>
            )}
          </Box>
        </Box>
      </Fade>
    </Modal>
  );
};

ImageLightbox.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  images: PropTypes.arrayOf(PropTypes.string),
  currentIndex: PropTypes.number.isRequired,
  onPrev: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired
};

const checkFiltersChanged = (current, last) => {
  if (!last) return false;
  return (
    current.searchQuery !== last.searchQuery ||
    current.minPrice !== last.minPrice ||
    current.maxPrice !== last.maxPrice ||
    current.disponibilitaFiltro !== last.disponibilitaFiltro ||
    current.statoProdottoFiltro !== last.statoProdottoFiltro ||
    current.certificatoFiltro !== last.certificatoFiltro ||
    JSON.stringify(current.selectedCategoryIds) !== JSON.stringify(last.selectedCategoryIds) ||
    JSON.stringify(current.selectedVendorIds) !== JSON.stringify(last.selectedVendorIds)
  );
};

// Immagine prodotto con effetto "ambient": la stessa immagine, sfocata e ingrandita
// dietro, crea un alone di colore che sfuma dolcemente nel bianco della card.
const AmbientProductImage = ({ src, onClick, size = 86 }) => {
  const [error, setError] = useState(false);

  if (!src || error) {
    return (
      <Box sx={{
        width: size,
        height: size,
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: "16px",
        backgroundColor: "#f8fafc",
        border: "1px dashed #cbd5e1"
      }}>
        <ImageNotSupportedIcon sx={{ fontSize: 22, color: "#94a3b8" }} />
      </Box>
    );
  }

  return (
    <Box
      onClick={onClick}
      sx={{
        position: "relative",
        width: size,
        height: size,
        flexShrink: 0,
        cursor: "pointer",
        transition: "transform 0.15s ease",
        "&:active": { transform: "scale(0.95)" }
      }}
    >
      {/* Alone ambient: contenuto in un riquadro poco più grande e ritagliato,
          così non invade il testo; il velo bianco ne maschera il bordo */}
      <Box sx={{ position: "absolute", inset: -8, borderRadius: "18px", overflow: "hidden" }}>
        <Box
          component="img"
          src={src}
          alt=""
          aria-hidden="true"
          onError={() => setError(true)}
          sx={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            filter: "blur(16px) saturate(1.5)",
            opacity: 0.5
          }}
        />
        <Box sx={{
          position: "absolute",
          inset: 0,
          background: "radial-gradient(closest-side, rgba(255,255,255,0) 30%, #ffffff 100%)"
        }} />
      </Box>
      {/* Immagine principale */}
      <Box
        component="img"
        src={src}
        alt="Prodotto"
        onError={() => setError(true)}
        sx={{
          position: "relative",
          width: "100%",
          height: "100%",
          objectFit: "contain",
          borderRadius: "12px"
        }}
      />
    </Box>
  );
};

AmbientProductImage.propTypes = {
  src: PropTypes.string,
  onClick: PropTypes.func,
  size: PropTypes.number
};

// Etichetta micro-maiuscoletto per i dettagli espansi delle card mobile
const mobileDetailLabelSx = {
  display: "block",
  color: "#94a3b8",
  fontWeight: 700,
  fontSize: "0.62rem",
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  mb: 0.25
};

const MobileProductCard = ({
  row,
  categoryPath,
  isSelected,
  isExpanded,
  handleSelectOne,
  handleToggleStatus,
  handleOpenStats,
  toggleCardExpansion,
  handleOpenLightbox
}) => {
  const { isActionVisible } = usePermissions();
  const logos = row.logos || [];

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: "18px",
        border: isSelected ? "2px solid #0b3d87" : "1px solid #e6ebf3",
        boxShadow: isSelected
          ? "0 12px 28px rgba(11, 61, 135, 0.18)"
          : "0 4px 14px rgba(15, 23, 42, 0.06)",
        transition: "border-color 0.25s ease, box-shadow 0.25s ease",
        overflow: "hidden"
      }}
    >
      <CardContent sx={{ p: 2, "&:last-child": { pb: 1.5 } }}>
        {/* Header: immagine ambient + info principali + selezione */}
        <Box display="flex" gap={1.5} alignItems="center">
          <AmbientProductImage
            src={logos[0]}
            onClick={() => logos.length > 0 && handleOpenLightbox(logos, 0)}
          />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle2" sx={{
              fontWeight: 700,
              color: "#1e293b",
              lineHeight: 1.25,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden"
            }}>
              {row.nomeProdotto || "Senza Nome"}
            </Typography>
            <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600, fontSize: "0.7rem", display: "block", mt: 0.25 }}>
              {row.nomeEcommerce || "-"}
            </Typography>
            <Typography sx={{ fontWeight: 800, color: "#0b3d87", fontSize: "1.05rem", mt: 0.5 }}>
              {row.prezzoVendita?.toFixed(2)} €
            </Typography>
          </Box>
          <Checkbox
            size="small"
            checked={isSelected}
            onChange={() => handleSelectOne(row.catalogListId)}
            sx={{ alignSelf: "flex-start", mt: -0.75, mr: -1 }}
          />
        </Box>

        {/* Stato e azioni */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mt={1.5} gap={1}>
          <Box display="flex" gap={0.75} alignItems="center" flexWrap="wrap">
            <StatusBadge quantity={row.disponibilita} />
            <ProductStateBadge
              attivo={isProdottoAttivo(row)}
              onClick={isActionVisible("gestioneProdotti", "cambiaStato")
                ? (e) => { e.stopPropagation(); handleToggleStatus(row); }
                : undefined}
            />
            <CategorizationBadge certificato={row.certificato} />
          </Box>
          <Box display="flex" flexShrink={0} gap={0.75}>
            {isActionVisible("gestioneProdotti", "statistiche") && (
              <ActionIconButton
                onClick={() => handleOpenStats(row)}
                icon={BarChartIcon}
                isMobile={true}
                mobileBgColor="#e8f0fe"
              />
            )}
            <IconButton
              size="small"
              onClick={() => toggleCardExpansion(row.catalogListId)}
              sx={{
                color: "#64748b",
                backgroundColor: "#f8fafc",
                transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.3s, background-color 0.2s',
                "&:hover": { backgroundColor: "#f1f5f9" }
              }}
            >
              <ExpandMoreIcon />
            </IconButton>
          </Box>
        </Box>

        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <Box mt={1.75} pt={1.75} sx={{ borderTop: "1px dashed #e0e6ed" }}>
            <Grid2 container spacing={1.25}>
              {logos.length > 1 && (
                <Grid2 size={12} display="flex" justifyContent="center" py={0.5}>
                  <LogoCarousel logos={logos} onImageClick={handleOpenLightbox} />
                </Grid2>
              )}
              <Grid2 size={6}>
                <Typography variant="caption" sx={mobileDetailLabelSx}>EAN</Typography>
                <Typography variant="body2" sx={{ fontSize: "0.8rem", color: "#1e293b" }}>{row.codiceEan || "-"}</Typography>
              </Grid2>
              <Grid2 size={6}>
                <Typography variant="caption" sx={mobileDetailLabelSx}>Categoria</Typography>
                <Typography variant="body2" sx={{ fontSize: "0.8rem", color: "#1e293b" }}>{categoryPath || "-"}</Typography>
              </Grid2>
              <Grid2 size={12}>
                <Typography variant="caption" sx={mobileDetailLabelSx}>Descrizione</Typography>
                <Typography variant="body2" sx={{ fontSize: "0.8rem", color: "#334155", lineHeight: 1.45 }}>{row.descrizione || "-"}</Typography>
              </Grid2>
            </Grid2>
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
};

MobileProductCard.propTypes = {
  row: PropTypes.object.isRequired,
  categoryPath: PropTypes.string,
  isSelected: PropTypes.bool.isRequired,
  isExpanded: PropTypes.bool,
  handleSelectOne: PropTypes.func.isRequired,
  handleToggleStatus: PropTypes.func.isRequired,
  handleOpenStats: PropTypes.func.isRequired,
  toggleCardExpansion: PropTypes.func.isRequired,
  handleOpenLightbox: PropTypes.func.isRequired
};

// Troncamento celle: descrizione max 2 righe (~80 caratteri), categoria max 3
// livelli (~50 caratteri). Oltre, la freccetta espande la riga.
const DESCRIPTION_MAX_CHARS = 80;
const CATEGORY_MAX_LEVELS = 3;
const CATEGORY_MAX_CHARS = 50;
const CATEGORY_SEPARATOR = " > ";

const categoryLevels = (path) => (path || "").split(CATEGORY_SEPARATOR).filter(Boolean);

const isDescriptionTruncated = (descrizione) => (descrizione || "").length > DESCRIPTION_MAX_CHARS;

const isCategoryTruncated = (path) =>
  categoryLevels(path).length > CATEGORY_MAX_LEVELS || (path || "").length > CATEGORY_MAX_CHARS;

// Percorso ridotto ai primi livelli, con ellissi se ne restano altri
const shortCategoryPath = (path) => {
  const levels = categoryLevels(path);
  if (levels.length <= CATEGORY_MAX_LEVELS) return path || "";
  return `${levels.slice(0, CATEGORY_MAX_LEVELS).join(CATEGORY_SEPARATOR)} …`;
};

// Clamp visivo applicato solo da chiuso
const clampedCellSx = (expanded, lines, maxWidth) => ({
  ...(expanded
    ? {}
    : {
      display: "-webkit-box",
      WebkitLineClamp: lines,
      WebkitBoxOrient: "vertical",
      overflow: "hidden",
      textOverflow: "ellipsis",
    }),
  wordBreak: "break-word",
  maxWidth,
});

const RowExpandToggle = ({ expanded, onToggle }) => (
  <IconButton
    size="small"
    onClick={onToggle}
    aria-label={expanded ? "Comprimi riga" : "Espandi riga"}
    sx={{
      p: 0.25,
      flexShrink: 0,
      color: "#64748b",
      transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
      transition: "transform 0.25s",
      "&:hover": { backgroundColor: "#f1f5f9" },
    }}
  >
    <ExpandMoreIcon fontSize="small" />
  </IconButton>
);

RowExpandToggle.propTypes = {
  expanded: PropTypes.bool,
  onToggle: PropTypes.func.isRequired,
};

const DesktopProductRow = ({
  row,
  categoryPath,
  index,
  isSelected,
  isExpanded,
  onToggleExpand,
  handleSelectOne,
  handleToggleStatus,
  handleOpenStats,
  handleOpenLightbox
}) => {
  const { isActionVisible } = usePermissions();

  return (
    <TableRow selected={isSelected} sx={mergedTableRowSx(index)}>
      <TableCell padding="checkbox">
        <Checkbox size="small" checked={isSelected} onChange={() => handleSelectOne(row.catalogListId)} />
      </TableCell>
      <TableCell sx={{ fontSize: "0.75rem", color: "#64748b" }}>
        {row.codiceEan ? (
          <Tooltip title={row.codiceEan}>
            <Box component="span" sx={{ display: 'inline-block', maxWidth: 90, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'bottom' }}>
              {row.codiceEan}
            </Box>
          </Tooltip>
        ) : "-"}
      </TableCell>
      <TableCell>
        <LogoCarousel logos={row.logos} onImageClick={handleOpenLightbox} />
      </TableCell>
      <TableCell sx={{ fontWeight: 600, fontSize: "0.85rem" }}>{row.nomeProdotto || "-"}</TableCell>
      <TableCell sx={{ fontSize: "0.8rem" }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.25 }}>
          {isExpanded ? (
            <Box sx={clampedCellSx(true, 2, 250)}>{row.descrizione || "-"}</Box>
          ) : (
            <Tooltip title={row.descrizione || ""} followCursor>
              <Box sx={clampedCellSx(false, 2, 250)}>{row.descrizione || "-"}</Box>
            </Tooltip>
          )}
          {isDescriptionTruncated(row.descrizione) && (
            <RowExpandToggle expanded={isExpanded} onToggle={onToggleExpand} />
          )}
        </Box>
      </TableCell>
      <TableCell sx={{ fontSize: "0.8rem" }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.25 }}>
          <Box sx={clampedCellSx(isExpanded, 2, 200)}>
            {(isExpanded ? categoryPath : shortCategoryPath(categoryPath)) || "-"}
          </Box>
          {isCategoryTruncated(categoryPath) && (
            <RowExpandToggle expanded={isExpanded} onToggle={onToggleExpand} />
          )}
        </Box>
      </TableCell>
      <TableCell sx={{ color: "#0b3d87", fontWeight: 500, fontSize: "0.8rem" }}>{row.nomeEcommerce || "-"}</TableCell>
      <TableCell sx={{ fontWeight: 700, fontSize: "0.85rem" }}>{row.prezzoVendita?.toFixed(2)} €</TableCell>
      <TableCell><StatusBadge quantity={row.disponibilita} /></TableCell>
      <TableCell align="center">
        <ProductStateBadge
          attivo={isProdottoAttivo(row)}
          onClick={isActionVisible("gestioneProdotti", "cambiaStato")
            ? () => handleToggleStatus(row)
            : undefined}
        />
      </TableCell>
      <TableCell align="center">
        <CategorizationBadge certificato={row.certificato} />
      </TableCell>
      <TableCell align="center">
        <Box display="flex" justifyContent="center" gap={0.5}>
          {isActionVisible("gestioneProdotti", "statistiche") && (
            <ActionIconButton
              title="Statistiche"
              onClick={() => handleOpenStats(row)}
              icon={BarChartIcon}
            />
          )}
        </Box>
      </TableCell>
    </TableRow>
  );
};

DesktopProductRow.propTypes = {
  row: PropTypes.object.isRequired,
  categoryPath: PropTypes.string,
  index: PropTypes.number,
  isSelected: PropTypes.bool.isRequired,
  isExpanded: PropTypes.bool,
  onToggleExpand: PropTypes.func.isRequired,
  handleSelectOne: PropTypes.func.isRequired,
  handleToggleStatus: PropTypes.func.isRequired,
  handleOpenStats: PropTypes.func.isRequired,
  handleOpenLightbox: PropTypes.func.isRequired
};

const CategoryAssociationModal = ({
  open,
  onClose,
  idNuovaCategoria,
  setIdNuovaCategoria,
  nomeNuovaCategoria,
  setNomeNuovaCategoria,
  categoryOptions,
  selectedIds,
  isUpdating,
  handleBulkUpdate
}) => {
  return (
    <Modal open={open} onClose={onClose} closeAfterTransition>
      <Fade in={open}>
        <Box sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "95%", sm: 500 },
          bgcolor: "background.paper",
          borderRadius: "24px",
          boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
          p: 4,
          overflow: "hidden"
        }}>
          <Box display="flex" alignItems="center" gap={2} mb={3}>
            <Box sx={{ p: 1.5, borderRadius: "12px", backgroundColor: "#eef2ff", color: "#4338ca" }}>
              <CloudSyncIcon />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800, color: "#1e293b", lineHeight: 1.2 }}>
                Associazione Categoria
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Stai associando {selectedIds.length} prodotti
              </Typography>
            </Box>
          </Box>

          <Box mb={4}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#475569", mb: 1, fontSize: "0.8rem", textTransform: "uppercase" }}>
              Seleziona Destinazione
            </Typography>
            <Autocomplete
              options={categoryOptions}
              getOptionLabel={(option) => option.label || ""}
              value={categoryOptions.find(o => o.id === idNuovaCategoria) || null}
              onChange={(e, val) => {
                setIdNuovaCategoria(val ? val.id : "");
                setNomeNuovaCategoria(val ? val.label : "");
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Cerca categoria..."
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "14px",
                      backgroundColor: "#f8fafc",
                      "& fieldset": { borderColor: "#e2e8f0" },
                      "&:hover fieldset": { borderColor: "#cbd5e1" }
                    }
                  }}
                />
              )}
            />
            {nomeNuovaCategoria && (
              <Box sx={{ mt: 1.5, p: 1.5, borderRadius: "10px", backgroundColor: "#f0fdf4", border: "1px solid #dcfce7" }}>
                <Typography variant="caption" sx={{ color: "#166534", fontWeight: 700 }}>
                  Destinazione: {nomeNuovaCategoria}
                </Typography>
              </Box>
            )}
          </Box>

          <Stack direction="row" spacing={2}>
            <Button
              fullWidth
              variant="outlined"
              onClick={onClose}
              sx={{ borderRadius: "14px", py: 1.2, textTransform: "none", fontWeight: 700, color: "#64748b", borderColor: "#e2e8f0" }}
            >
              Annulla
            </Button>
            <Button
              fullWidth
              variant="contained"
              onClick={handleBulkUpdate}
              disabled={isUpdating || !idNuovaCategoria}
              sx={{
                borderRadius: "14px",
                py: 1.2,
                textTransform: "none",
                fontWeight: 700,
                backgroundColor: "#0b3d87",
                "&:hover": { backgroundColor: "#082d63" }
              }}
            >
              {isUpdating ? <CircularProgress size={24} color="inherit" /> : "Conferma Associazione"}
            </Button>
          </Stack>
        </Box>
      </Fade>
    </Modal>
  );
};

CategoryAssociationModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  idNuovaCategoria: PropTypes.any,
  setIdNuovaCategoria: PropTypes.func.isRequired,
  nomeNuovaCategoria: PropTypes.string,
  setNomeNuovaCategoria: PropTypes.func.isRequired,
  categoryOptions: PropTypes.array.isRequired,
  selectedIds: PropTypes.array.isRequired,
  isUpdating: PropTypes.bool.isRequired,
  handleBulkUpdate: PropTypes.func.isRequired
};

const StatsModal = ({
  open,
  onClose,
  selectedProductForStats,
  statsPreset,
  setStatsPreset,
  statsStartDate,
  setStatsStartDate,
  statsEndDate,
  setStatsEndDate,
  handleDateRangeChange,
  loadingStats,
  productStats
}) => {
  return (
    <Modal open={open} onClose={onClose} closeAfterTransition>
      <Fade in={open}>
        <Box sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "95%", sm: 550 },
          bgcolor: "background.paper",
          borderRadius: "28px",
          boxShadow: "0 25px 50px rgba(0,0,0,0.15)",
          p: 4,
          maxHeight: "90vh",
          overflowY: "auto"
        }}>
          <Box display="flex" alignItems="center" gap={2} mb={4}>
            <Box sx={{
              p: 1.5,
              borderRadius: "12px",
              backgroundColor: "#eef2ff",
              color: "#4338ca",
              display: "flex"
            }}>
              <BarChartIcon />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 800, color: "#1e293b", lineHeight: 1.2 }}>
                Analisi Performance
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Visualizza metriche per {selectedProductForStats?.nomeProdotto}
              </Typography>
            </Box>
          </Box>

          <Box mb={3}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#475569", mb: 1.5, fontSize: "0.8rem", textTransform: "uppercase" }}>
              Periodo di Analisi
            </Typography>
            <Stack direction="row" spacing={1} mb={2} flexWrap="wrap" useFlexGap gap={1}>
              {[
                { id: "all", label: "Sempre", days: null },
                { id: "today", label: "Oggi", days: 0 },
                { id: "7days", label: "7 Giorni", days: 7 },
                { id: "30days", label: "30 Giorni", days: 30 },
                { id: "custom", label: "Personalizzato", days: -1 }
              ].map((p) => (
                <Chip
                  key={p.id}
                  label={p.label}
                  onClick={() => {
                    if (p.id === "custom") {
                      setStatsPreset("custom");
                      return;
                    }
                    const end = new Date().toISOString().split('T')[0];
                    let start = null;
                    if (p.days !== null) {
                      const d = new Date();
                      d.setDate(d.getDate() - p.days);
                      start = d.toISOString().split('T')[0];
                    }
                    handleDateRangeChange(p.id, start, end);
                  }}
                  sx={{
                    borderRadius: "8px",
                    fontWeight: 600,
                    backgroundColor: statsPreset === p.id ? "#0b3d87" : "#f1f5f9",
                    color: statsPreset === p.id ? "white" : "#475569",
                    "&:hover": { backgroundColor: statsPreset === p.id ? "#0b3d87" : "#e2e8f0" }
                  }}
                />
              ))}
            </Stack>

            {statsPreset === "custom" && (
              <Fade in={true}>
                <Box display="flex" gap={2} mt={2}>
                  <TextField
                    label="Da"
                    type="date"
                    fullWidth
                    size="small"
                    value={statsStartDate}
                    onChange={(e) => setStatsStartDate(e.target.value)}
                    slotProps={{ inputLabel: { shrink: true } }}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
                  />
                  <TextField
                    label="A"
                    type="date"
                    fullWidth
                    size="small"
                    value={statsEndDate}
                    onChange={(e) => setStatsEndDate(e.target.value)}
                    slotProps={{ inputLabel: { shrink: true } }}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
                  />
                  <Button
                    variant="contained"
                    onClick={() => handleDateRangeChange("custom", statsStartDate, statsEndDate)}
                    sx={{ borderRadius: "10px", textTransform: "none", backgroundColor: "#0b3d87" }}
                  >
                    Applica
                  </Button>
                </Box>
              </Fade>
            )}
          </Box>

          <Divider sx={{ mb: 3 }} />

          {loadingStats ? (
            <Box display="flex" flexDirection="column" alignItems="center" py={6} gap={2}>
              <CircularProgress size={40} sx={{ color: "#0b3d87" }} />
              <Typography variant="body2" color="textSecondary">Analisi in corso...</Typography>
            </Box>
          ) : (
            <Box>
              <Grid2 container spacing={2} mb={3}>
                <Grid2 size={6}>
                  <Box sx={{ p: 2.5, borderRadius: "20px", border: "1px solid #e2e8f0", textAlign: "center", backgroundColor: "#f8fafc" }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                      Click Nel Periodo
                    </Typography>
                    <Typography variant="h3" sx={{ fontWeight: 850, color: "#0b3d87", mt: 1 }}>
                      {productStats?.numClickGenerati || 0}
                    </Typography>
                  </Box>
                </Grid2>
                <Grid2 size={6}>
                  <Box sx={{ p: 2.5, borderRadius: "20px", border: "1px solid #dcfce7", textAlign: "center", backgroundColor: "#f0fdf4" }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: "#166534", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                      Saldo Generato
                    </Typography>
                    <Typography variant="h3" sx={{ fontWeight: 850, color: "#15803d", mt: 1 }}>
                      € {productStats?.saldoGenerato?.toFixed(2) || "0.00"}
                    </Typography>
                  </Box>
                </Grid2>
              </Grid2>

              <Alert severity="info" sx={{ borderRadius: "12px", "& .MuiAlert-message": { fontSize: "0.75rem" } }}>
                Il saldo è calcolato moltiplicando i click del periodo per il prezzo attuale della categoria del prodotto.
              </Alert>

              <Box mt={4} display="flex" justifyContent="center">
                <Button
                  variant="outlined"
                  onClick={onClose}
                  sx={{
                    borderRadius: "12px",
                    px: 5,
                    py: 1,
                    textTransform: "none",
                    fontWeight: 800,
                    borderColor: "#e2e8f0",
                    color: "#64748b",
                    "&:hover": { backgroundColor: "#f8fafc", borderColor: "#cbd5e1" }
                  }}
                >
                  Chiudi Report
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      </Fade>
    </Modal>
  );
};

StatsModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  selectedProductForStats: PropTypes.object,
  statsPreset: PropTypes.string.isRequired,
  setStatsPreset: PropTypes.func.isRequired,
  statsStartDate: PropTypes.string,
  setStatsStartDate: PropTypes.func.isRequired,
  statsEndDate: PropTypes.string,
  setStatsEndDate: PropTypes.func.isRequired,
  handleDateRangeChange: PropTypes.func.isRequired,
  loadingStats: PropTypes.bool.isRequired,
  productStats: PropTypes.object
};

const GestioneProdotti = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Header sticky agganciato alla finestra (ricerca + header + tabella fusi)
  const { stickySentinelRef } = useStickyTableHeader();

  const [prodotti, setProdotti] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalElements, setTotalElements] = useState(0);
  const { showSuccess } = useUI();

  // Filtri e Paginazione
  const [anchorEl, setAnchorEl] = useState(null);
  const searchBarRef = useRef(null);

  const handleToggleFilters = (event) => {
    if (showAdvancedFilters) {
      setShowAdvancedFilters(false);
      setAnchorEl(null);
    } else {
      setShowAdvancedFilters(true);
      setAnchorEl(searchBarRef.current);
    }
  };

  const handleCloseFilters = () => {
    setShowAdvancedFilters(false);
    setAnchorEl(null);
  };

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [disponibilitaFiltro, setDisponibilitaFiltro] = useState("");
  const [statoProdottoFiltro, setStatoProdottoFiltro] = useState(""); // "", "ATTIVO", "NON_ATTIVO"
  const [certificatoFiltro, setCertificatoFiltro] = useState(""); // "", "true", "false"
  const [sorts, setSorts] = useState([{ campo: "nome", direzione: "ASC" }]);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState([]);

  // Selezione Bulk e Modale Associazione
  const [selectedIds, setSelectedIds] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [idNuovaCategoria, setIdNuovaCategoria] = useState("");
  const [nomeNuovaCategoria, setNomeNuovaCategoria] = useState("");
  const [isUpdating, setIsUpdating] = useState(false);
  const [expandedCards, setExpandedCards] = useState({});
  // Righe desktop espanse: mostrano descrizione e categoria per intero
  const [expandedRows, setExpandedRows] = useState({});

  // Categorie e Ecommerce per Autocomplete
  const [categories, setCategories] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [selectedVendorIds, setSelectedVendorIds] = useState([]);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [vendorOptions, setVendorOptions] = useState([]);

  // Statistiche
  const [statsModalOpen, setStatsModalOpen] = useState(false);
  const [selectedProductForStats, setSelectedProductForStats] = useState(null);
  const [productStats, setProductStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);
  const [statsPreset, setStatsPreset] = useState("all");
  const [statsStartDate, setStatsStartDate] = useState("");
  const [statsEndDate, setStatsEndDate] = useState("");

  // Lightbox Images
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImages, setLightboxImages] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  // Modale Conferma Cambio Stato
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [productToToggle, setProductToToggle] = useState(null);

  // Ref per tracciare l'ultima chiamata ed evitare ridondanze
  const lastParamsRef = useRef(null);

  const fetchCategories = useCallback(async () => {
    try {
      const data = await getAllCategories();
      setCategories(data || []);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  }, []);

  const fetchVendors = useCallback(async () => {
    try {
      const data = await getAllVendors();
      if (data) {
        const options = data.map(v => ({
          id: v.vendorListId,
          label: v.businessName
        })).sort((a, b) => a.label.localeCompare(b.label));
        setVendorOptions(options);
      }
    } catch (error) {
      console.error("Error fetching vendors:", error);
    }
  }, []);

  // Mappa categoryId -> percorso completo ("Elettronica > Smartphone") dall'albero ufficiale
  const categoryPathById = useMemo(() => {
    const catMap = new Map(categories.map(c => [c.categoryId, c]));
    const buildPath = (catId) => {
      const cat = catMap.get(catId);
      if (!cat) return "";
      const parentPath = cat.parentId ? buildPath(cat.parentId) : "";
      return parentPath ? `${parentPath} > ${cat.nome}` : cat.nome;
    };
    const paths = new Map();
    categories.forEach(c => paths.set(c.categoryId, buildPath(c.categoryId)));
    return paths;
  }, [categories]);

  // Opzioni per la modale di associazione, derivate dai percorsi
  useEffect(() => {
    if (categoryPathById.size === 0) return;
    const options = [...categoryPathById.entries()]
      .map(([id, label]) => ({ id, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
    setCategoryOptions(options);
  }, [categoryPathById]);

  const fetchProdotti = useCallback(async (currentParams) => {
    setLoading(true);
    try {
      const { searchQuery, minPrice, maxPrice, disponibilitaFiltro, statoProdottoFiltro, certificatoFiltro, page, rowsPerPage, sorts, selectedCategoryIds, selectedVendorIds } = currentParams;

      const request = {
        testoRicerca: searchQuery,
        paroleRicerca: searchQuery.trim() ? searchQuery.split(/\s+/).filter(w => w.length > 0) : [],
        prezzoMin: minPrice ? Number.parseFloat(minPrice) : null,
        prezzoMax: maxPrice ? Number.parseFloat(maxPrice) : null,
        statoDisponibilita: disponibilitaFiltro || null,
        statoProdotto: statoProdottoFiltro || null, // Stato prodotto: attivo (non disatt./non elim.)
        certificato: certificatoFiltro === "" ? null : (certificatoFiltro === "true"), // Categorizzazione
        numeroPagina: page,
        dimensionePagina: rowsPerPage,
        ordinamento: sorts,
        categoryIds: selectedCategoryIds,
        vendorIds: selectedVendorIds
      };

      const data = await searchProducts(request);
      setProdotti(data.products || []);
      setTotalElements(data.totalElements || 0);
    } catch (error) {
      console.error("Error searching products:", error);
    } finally {
      setTimeout(() => setLoading(false), 300);
    }
  }, []);

  // Effect unico per caricamento categorie e prodotti
  useEffect(() => {
    // Carica categorie e vendor se non presenti
    if (categories.length === 0) {
      fetchCategories();
    }
    if (vendorOptions.length === 0) {
      fetchVendors();
    }

    // Debounce per la ricerca prodotti
    const handler = setTimeout(() => {
      const isQueryLongEnough = searchQuery.length >= 3 || searchQuery.length === 0;
      if (!isQueryLongEnough) return;

      const currentParams = {
        searchQuery, minPrice, maxPrice, disponibilitaFiltro, statoProdottoFiltro, certificatoFiltro,
        page, rowsPerPage, sorts, selectedCategoryIds, selectedVendorIds
      };

      // Verifica se i parametri sono effettivamente cambiati rispetto all'ultima chiamata
      const lastParams = lastParamsRef.current;
      const hasChanged = !lastParams || JSON.stringify(currentParams) !== JSON.stringify(lastParams);

      if (hasChanged) {
        // Se i filtri sono cambiati, resetta la pagina a 0 (tranne se è già 0)
        const filtersChanged = checkFiltersChanged(currentParams, lastParams);

        if (filtersChanged && page !== 0) {
          setPage(0);
          return;
        }

        fetchProdotti(currentParams);
        lastParamsRef.current = currentParams;
      }
    }, 300);

    return () => clearTimeout(handler);
  }, [fetchProdotti, fetchCategories, fetchVendors, categories.length, vendorOptions.length, searchQuery, minPrice, maxPrice, disponibilitaFiltro, statoProdottoFiltro, certificatoFiltro, page, rowsPerPage, sorts, selectedCategoryIds, selectedVendorIds]);

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedIds(prodotti.map(p => p.catalogListId));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (id) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const handleOpenModal = () => {
    if (selectedIds.length === 0) return;
    if (categories.length === 0) fetchCategories();
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setIdNuovaCategoria("");
    setNomeNuovaCategoria("");
  };

  const handleRequestSort = (event, property) => {
    setSorts(prev => {
      const existing = prev.find(s => s.campo === property);
      let newDir = "ASC";
      if (existing) {
        newDir = existing.direzione === "ASC" ? "DESC" : "ASC";
      }
      return [{ campo: property, direzione: newDir }];
    });
    setPage(0);
  };

  const handleBulkAssociate = async () => {
    if (!idNuovaCategoria) {
      return;
    }

    setIsUpdating(true);
    try {
      await associateProducts(selectedIds, idNuovaCategoria, nomeNuovaCategoria);
      handleCloseModal();
      setSelectedIds([]);
      showSuccess("Prodotti associati con successo!");
      fetchProdotti();
    } catch (error) {
      console.error("Error during bulk association:", error);
    } finally {
      setIsUpdating(false);
    }
  };

  const toggleCardExpansion = (id) => {
    setExpandedCards(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const toggleRowExpansion = (id) => {
    setExpandedRows(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const fetchStats = useCallback(async (prodotto, start, end) => {
    setLoadingStats(true);
    try {
      const data = await getProductStats(prodotto.catalogListId, prodotto.nomeProdotto, start, end);
      setProductStats(data);
    } catch (error) {
      console.error("Error fetching product stats:", error);
    } finally {
      setLoadingStats(false);
    }
  }, []);

  const handleOpenStats = (prodotto) => {
    setSelectedProductForStats(prodotto);
    setStatsModalOpen(true);
    setStatsPreset("all");
    setStatsStartDate("");
    setStatsEndDate("");
    fetchStats(prodotto, null, null);
  };

  const handleDateRangeChange = (preset, start, end) => {
    setStatsPreset(preset);
    setStatsStartDate(start || "");
    setStatsEndDate(end || "");
    fetchStats(selectedProductForStats, start, end);
  };

  const handleCloseStats = () => {
    setStatsModalOpen(false);
    setProductStats(null);
    setSelectedProductForStats(null);
  };

  const handleOpenLightbox = (images, index) => {
    setLightboxImages(images);
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const handleNextLightbox = () => {
    setLightboxIndex((prev) => (prev + 1) % lightboxImages.length);
  };

  const handlePrevLightbox = () => {
    setLightboxIndex((prev) => (prev - 1 + lightboxImages.length) % lightboxImages.length);
  };

  const handleToggleStatus = (prodotto) => {
    setProductToToggle(prodotto);
    setConfirmModalOpen(true);
  };

  const confirmToggleStatus = async () => {
    if (!productToToggle) return;
    try {
      const attivo = isProdottoAttivo(productToToggle);
      // Stato prodotto unico: attiva azzera entrambi i flag, disattiva li imposta entrambi a 1
      await updateProduct({
        catalogListId: productToToggle.catalogListId,
        isDisattivato: attivo ? 1 : 0,
        isDeleted: attivo ? 1 : 0
      });
      showSuccess(`Stato prodotto aggiornato a ${attivo ? 'Non Attivo' : 'Attivo'}`);
      const currentParams = {
        searchQuery, minPrice, maxPrice, disponibilitaFiltro, statoProdottoFiltro, certificatoFiltro,
        page, rowsPerPage, sorts, selectedCategoryIds, selectedVendorIds
      };
      fetchProdotti(currentParams);
    } catch (error) {
      console.error("Error toggling product status:", error);
    } finally {
      setConfirmModalOpen(false);
      setProductToToggle(null);
    }
  };

  const renderMobileCards = () => (
    <Stack spacing={2} sx={{ mb: 4, minHeight: '300px' }}>
      {prodotti.length === 0 && !loading ? (
        <Typography textAlign="center" color="text.secondary" py={3}>
          {searchQuery.length > 0 && searchQuery.length < 3 ? "Inserisci almeno 3 caratteri per cercare." : "Nessun prodotto trovato."}
        </Typography>
      ) : (
        prodotti.map((row) => (
          <MobileProductCard
            key={row.catalogListId}
            row={row}
            categoryPath={categoryPathById.get(row.categoryListId) || ""}
            isSelected={selectedIds.includes(row.catalogListId)}
            isExpanded={expandedCards[row.catalogListId]}
            handleSelectOne={handleSelectOne}
            handleToggleStatus={handleToggleStatus}
            handleOpenStats={handleOpenStats}
            toggleCardExpansion={toggleCardExpansion}
            handleOpenLightbox={handleOpenLightbox}
          />
        ))
      )}
    </Stack>
  );

  // Stato attuale del prodotto in fase di conferma (per testi/colori della modale)
  const productToToggleAttivo = productToToggle ? isProdottoAttivo(productToToggle) : false;

  const hasActiveFilters = searchQuery.length >= 3 || minPrice !== "" || maxPrice !== "" || disponibilitaFiltro !== "" || statoProdottoFiltro !== "" || certificatoFiltro !== "" || selectedCategoryIds.length > 0 || selectedVendorIds.length > 0;

  const handleClearFilters = () => {
    setSearchQuery("");
    setMinPrice("");
    setMaxPrice("");
    setDisponibilitaFiltro("");
    setStatoProdottoFiltro("");
    setCertificatoFiltro("");
    setSelectedCategoryIds([]);
    setSelectedVendorIds([]);
    setSorts([{ campo: "nome", direzione: "ASC" }]);
    setPage(0);
    fetchProdotti();
  };

  const searchAndFilters = (
    <Box ref={searchBarRef} sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "nowrap", width: { xs: "100%", md: "auto" } }}>
      <TextField
        placeholder="Cerca per descrizione, ean..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        sx={{ flex: { xs: 1, md: "0 0 auto" }, width: { xs: "auto", md: 320 }, minWidth: 0 }}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "#0b3d87", opacity: 0.6 }} />
              </InputAdornment>
            ),
            sx: {
              borderRadius: "16px",
              backgroundColor: "#f8fafc",
              height: "54px",
              fontSize: "0.9rem",
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              "&:hover": { backgroundColor: "#f1f5f9", boxShadow: "0 4px 12px rgba(0,0,0,0.03)" },
              "&.Mui-focused": { backgroundColor: "white", boxShadow: "0 0 0 4px rgba(11, 61, 135, 0.08), 0 4px 12px rgba(11, 61, 135, 0.05)", "& fieldset": { borderColor: "#0b3d87 !important", borderWidth: "2px" } },
              "& fieldset": { borderColor: "#e2e8f0 !important" }
            }
          },
        }}
      />
      <Tooltip title="Filtri Avanzati" copyable={false}>
        <IconButton
          onClick={handleToggleFilters}
          sx={{
            flexShrink: 0,
            backgroundColor: showAdvancedFilters ? "#0b3d87" : "rgba(0, 0, 0, 0.04)",
            color: showAdvancedFilters ? "white" : "#475569",
            borderRadius: "16px",
            width: "54px",
            height: "54px",
            transition: "all 0.3s ease",
            "&:hover": { backgroundColor: showAdvancedFilters ? "#082d63" : "rgba(0, 0, 0, 0.08)" }
          }}
        >
          {showAdvancedFilters ? <ExpandLessIcon /> : <TuneIcon />}
        </IconButton>
      </Tooltip>

      {hasActiveFilters && (
        <Tooltip title="Pulisci filtri" copyable={false}>
          <IconButton
            onClick={handleClearFilters}
            sx={{
              flexShrink: 0,
              backgroundColor: "rgba(244, 67, 54, 0.08)",
              color: "#f44336",
              borderRadius: "16px",
              width: "54px",
              height: "54px",
              transition: "all 0.3s ease",
              "&:hover": { backgroundColor: "#f44336", color: "white" }
            }}
          >
            <FilterAltOffIcon />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );

  return (
    <Box sx={adminPageShellSx}>
      <Fade in={true} timeout={800}>
        <Box>
          <AdminPageHeader
            title="Gestione Prodotti"
            subtitle="Visualizza e gestisci l'intero catalogo prodotti."
            actions={isMobile ? undefined : searchAndFilters}
          />

          {isMobile && (
            <Box
              sx={{
                position: "sticky",
                top: 15,
                zIndex: 1100,
                mb: 2,
                backgroundColor: "rgba(255, 255, 255, 0.85)",
                backdropFilter: "blur(16px) saturate(180%)",
                WebkitBackdropFilter: "blur(16px) saturate(180%)",
                borderRadius: "18px",
                p: 1,
                boxShadow: "0 8px 24px -10px rgba(31, 38, 135, 0.18)",
              }}
            >
              {searchAndFilters}
            </Box>
          )}


          {/* Sentinella per rilevare lo stato sticky della barra filtri */}
          <div ref={stickySentinelRef} style={{ height: "1px", marginTop: "-1px" }} />

          {/* Advanced Filters Popover */}
          <Popover
            open={showAdvancedFilters}
            anchorEl={anchorEl}
            onClose={handleCloseFilters}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            slotProps={{
              paper: {
                sx: {
                  width: searchBarRef.current ? searchBarRef.current.clientWidth : 'auto',
                  mt: 1,
                  p: 2,
                  boxSizing: 'border-box',
                  borderRadius: "12px",
                  boxShadow: "0 10px 40px rgba(11, 61, 135, 0.15)",
                  border: "1px solid rgba(226, 232, 240, 0.8)",
                  maxHeight: "60vh",
                  overflowX: 'hidden'
                }
              }
            }}
          >
            <Stack spacing={2.5}>


              <Box>
                <CategoryTreeFilter
                  categories={categories}
                  selectedCategoryIds={selectedCategoryIds}
                  onChange={setSelectedCategoryIds}
                />
              </Box>

              <Box>
                <Autocomplete
                  multiple
                  limitTags={1}
                  options={vendorOptions}
                  getOptionLabel={(option) => option.label}
                  value={vendorOptions.filter(opt => selectedVendorIds.includes(opt.id))}
                  onChange={(event, newValue) => {
                    setSelectedVendorIds(newValue.map(v => v.id));
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Filtra per Ecommerce"
                      size="small"
                      InputLabelProps={{ sx: { fontSize: '0.85rem' } }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "12px",
                          backgroundColor: "#f8fafc",
                          fontSize: "0.85rem",
                          transition: "all 0.2s ease-in-out",
                          "&:hover": { backgroundColor: "#f1f5f9" },
                          "&.Mui-focused": { backgroundColor: "white", "& fieldset": { borderColor: "#0b3d87 !important" } },
                          "& fieldset": { borderColor: "#e2e8f0 !important" }
                        }
                      }}
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => {
                      const { key, ...tagProps } = getTagProps({ index });
                      return (
                        <Chip
                          key={option.id}
                          label={option.label}
                          size="small"
                          {...tagProps}
                          sx={{
                            borderRadius: "8px",
                            backgroundColor: "#e2e8f0",
                            color: "#1e293b",
                            fontSize: "0.75rem",
                            fontWeight: 600,
                            "&:hover": { backgroundColor: "#cbd5e1" }
                          }}
                        />
                      );
                    })
                  }
                />
              </Box>

              <Stack direction="row" spacing={2}>
                <TextField
                  fullWidth
                  label="Min €"
                  type="number"
                  value={minPrice}
                  onChange={(e) => setMinPrice(e.target.value)}
                  size="small"
                  InputLabelProps={{ sx: { fontSize: "0.85rem" } }}
                  slotProps={{
                    input: {
                      sx: { borderRadius: "12px", backgroundColor: "#f8fafc", fontSize: "0.85rem" }
                    }
                  }}
                />
                <TextField
                  fullWidth
                  label="Max €"
                  type="number"
                  value={maxPrice}
                  onChange={(e) => setMaxPrice(e.target.value)}
                  size="small"
                  InputLabelProps={{ sx: { fontSize: "0.85rem" } }}
                  slotProps={{
                    input: {
                      sx: { borderRadius: "12px", backgroundColor: "#f8fafc", fontSize: "0.85rem" }
                    }
                  }}
                />
              </Stack>

              <FormControl fullWidth size="small">
                <InputLabel sx={{ color: "#64748b", fontSize: "0.85rem" }}>Stato prodotto</InputLabel>
                <Select
                  value={statoProdottoFiltro}
                  onChange={(e) => setStatoProdottoFiltro(e.target.value)}
                  label="Stato prodotto"
                  sx={{
                    borderRadius: "12px",
                    backgroundColor: "#f8fafc",
                    fontSize: "0.85rem",
                    "&:hover": { backgroundColor: "#f1f5f9" },
                    "&.Mui-focused": { backgroundColor: "white", "& fieldset": { borderColor: "#0b3d87 !important" } }
                  }}
                >
                  <MenuItem value="">Tutti</MenuItem>
                  <MenuItem value="ATTIVO">Attivo</MenuItem>
                  <MenuItem value="NON_ATTIVO">Non Attivo</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth size="small">
                <InputLabel sx={{ color: "#64748b", fontSize: "0.85rem" }}>Categorizzazione</InputLabel>
                <Select
                  value={certificatoFiltro}
                  onChange={(e) => setCertificatoFiltro(e.target.value)}
                  label="Categorizzazione"
                  sx={{
                    borderRadius: "12px",
                    backgroundColor: "#f8fafc",
                    fontSize: "0.85rem",
                    "&:hover": { backgroundColor: "#f1f5f9" },
                    "&.Mui-focused": { backgroundColor: "white", "& fieldset": { borderColor: "#0b3d87 !important" } }
                  }}
                >
                  <MenuItem value="">Tutte</MenuItem>
                  <MenuItem value="true">Certificato</MenuItem>
                  <MenuItem value="false">Non certificato</MenuItem>
                </Select>
              </FormControl>

              <Stack direction="row" spacing={1} alignItems="center">
                <FormControl sx={{ flex: 1 }} size="small">
                  <InputLabel sx={{ color: "#64748b", fontSize: "0.85rem" }}>Disponibilità</InputLabel>
                  <Select
                    value={disponibilitaFiltro}
                    onChange={(e) => setDisponibilitaFiltro(e.target.value)}
                    label="Disponibilità"
                    sx={{
                      borderRadius: "12px",
                      backgroundColor: "#f8fafc",
                      fontSize: "0.85rem",
                      "&:hover": { backgroundColor: "#f1f5f9" },
                      "&.Mui-focused": { backgroundColor: "white", "& fieldset": { borderColor: "#0b3d87 !important" } }
                    }}
                  >
                    <MenuItem value="">Tutte le disp.</MenuItem>
                    <MenuItem value="AVAILABLE">Disponibile</MenuItem>
                    <MenuItem value="OUT_OF_STOCK">Esaurito</MenuItem>
                  </Select>
                </FormControl>
              </Stack>
            </Stack>
          </Popover>

          {/* Bulk Action Button (Centered) */}
          {selectedIds.length > 0 && (
            <Fade in={true}>
              <Box display="flex" justifyContent="center" mt={1} mb={1}>
                <Button
                  variant="contained"
                  startIcon={<TaskAltIcon />}
                  sx={{
                    backgroundColor: "#0b3d87", borderRadius: "10px", px: 3, py: 0.8, textTransform: "none", fontWeight: 700, fontSize: "0.85rem",
                    boxShadow: "0 4px 12px rgba(11, 61, 135, 0.15)"
                  }}
                  onClick={handleOpenModal}
                >
                  Associa ({selectedIds.length})
                </Button>
              </Box>
            </Fade>
          )}

          {/* Table or Cards Container */}
          <Box sx={{ position: 'relative', mt: selectedIds.length > 0 ? 0 : 1.5, minHeight: '300px' }}>
            <TableLoadingOverlay loading={loading} />


            {isMobile ? renderMobileCards() : (
              <AdminMergedTable
                tableLoading={loading}
                size="small"
                tableLayout="fixed"
                head={(
                  <TableHead sx={mergedTableHeadSx(0)}>
                    <TableRow>
                      <TableCell padding="checkbox" sx={{ width: '4%' }}>
                        <Checkbox
                          size="small"
                          indeterminate={selectedIds.length > 0 && selectedIds.length < prodotti.length}
                          checked={prodotti.length > 0 && selectedIds.length === prodotti.length}
                          onChange={handleSelectAll}
                          sx={{ color: "white !important" }}
                        />
                      </TableCell>
                      <TableCell align="center" sx={{ width: '9%' }}>EAN</TableCell>
                      <TableCell align="center" sx={{ width: 120 }}>Logo</TableCell>
                      <TableCell align="center" sx={{ width: '14%' }}>
                        <TableSortLabel
                          active={sorts.some(s => s.campo === "nome")}
                          direction={sorts.find(s => s.campo === "nome")?.direzione.toLowerCase() || "asc"}
                          onClick={(e) => handleRequestSort(e, "nome")}
                          sx={{ color: "white !important", '& .MuiTableSortLabel-icon': { color: "white !important" } }}
                        >
                          Prodotto
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="center" sx={{ width: '13%' }}>
                        <TableSortLabel
                          active={sorts.some(s => s.campo === "descrizione")}
                          direction={sorts.find(s => s.campo === "descrizione")?.direzione.toLowerCase() || "asc"}
                          onClick={(e) => handleRequestSort(e, "descrizione")}
                          sx={{ color: "white !important", '& .MuiTableSortLabel-icon': { color: "white !important" } }}
                        >
                          Descrizione
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="center" sx={{ width: '8%' }}>Categoria</TableCell>
                      <TableCell align="center" sx={{ width: '8%' }}>Ecommerce</TableCell>
                      <TableCell align="center" sx={{ width: '8%' }}>
                        <TableSortLabel
                          active={sorts.some(s => s.campo === "prezzo_vendita")}
                          direction={sorts.find(s => s.campo === "prezzo_vendita")?.direzione.toLowerCase() || "asc"}
                          onClick={(e) => handleRequestSort(e, "prezzo_vendita")}
                          sx={{ color: "white !important", '& .MuiTableSortLabel-icon': { color: "white !important" } }}
                        >
                          Prezzo
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="center" sx={{ width: '10%' }}>
                        <TableSortLabel
                          active={sorts.some(s => s.campo === "disponibilita")}
                          direction={sorts.find(s => s.campo === "disponibilita")?.direzione.toLowerCase() || "asc"}
                          onClick={(e) => handleRequestSort(e, "disponibilita")}
                          sx={{ color: "white !important", '& .MuiTableSortLabel-icon': { color: "white !important" } }}
                        >
                          Disponibilità
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="center" sx={{ width: '9%' }}>Stato</TableCell>
                      <TableCell align="center" sx={{ width: '9%' }}>Certificato</TableCell>
                      <TableCell align="center" sx={{ width: '6%' }}>Azioni</TableCell>
                    </TableRow>
                  </TableHead>
                )}
                body={(
                  <TableBody>
                    {prodotti.length === 0 && !loading ? (
                      <TableRow>
                        <TableCell colSpan={12} align="center" sx={{ py: 6 }}>
                          <Typography variant="body2" color="textSecondary">Nessun prodotto trovato.</Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      prodotti.map((row, index) => (
                        <DesktopProductRow
                          key={row.catalogListId}
                          row={row}
                          categoryPath={categoryPathById.get(row.categoryListId) || ""}
                          index={index}
                          isSelected={selectedIds.includes(row.catalogListId)}
                          isExpanded={!!expandedRows[row.catalogListId]}
                          onToggleExpand={() => toggleRowExpansion(row.catalogListId)}
                          handleSelectOne={handleSelectOne}
                          handleToggleStatus={handleToggleStatus}
                          handleOpenStats={handleOpenStats}
                          handleOpenLightbox={handleOpenLightbox}
                        />
                      ))
                    )}
                  </TableBody>
                )}
              />
            )}
          </Box>

          {/* Barra di paginazione condivisa */}
          <AdminTablePagination
            rowsPerPage={rowsPerPage}
            setRowsPerPage={setRowsPerPage}
            page={page}
            setPage={setPage}
            totalPages={Math.ceil(totalElements / rowsPerPage) || 1}
            countLabel="Prodotti"
            countValue={totalElements}
          />
        </Box>
      </Fade>

      <ScrollTopFab />

      {/* Modale Associazione Categoria */}
      <CategoryAssociationModal
        open={modalOpen}
        onClose={handleCloseModal}
        idNuovaCategoria={idNuovaCategoria}
        setIdNuovaCategoria={setIdNuovaCategoria}
        nomeNuovaCategoria={nomeNuovaCategoria}
        setNomeNuovaCategoria={setNomeNuovaCategoria}
        categoryOptions={categoryOptions}
        selectedIds={selectedIds}
        isUpdating={isUpdating}
        handleBulkUpdate={handleBulkAssociate}
      />

      {/* Modale Statistiche Prodotto */}
      <StatsModal
        open={statsModalOpen}
        onClose={handleCloseStats}
        selectedProductForStats={selectedProductForStats}
        statsPreset={statsPreset}
        setStatsPreset={setStatsPreset}
        statsStartDate={statsStartDate}
        setStatsStartDate={setStatsStartDate}
        statsEndDate={statsEndDate}
        setStatsEndDate={setStatsEndDate}
        handleDateRangeChange={handleDateRangeChange}
        loadingStats={loadingStats}
        productStats={productStats}
      />

      {/* Image Lightbox */}
      <ImageLightbox
        open={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        images={lightboxImages}
        currentIndex={lightboxIndex}
        onPrev={handlePrevLightbox}
        onNext={handleNextLightbox}
      />

      {/* Modale Conferma Cambio Stato */}
      <ConfirmationModal
        open={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        onConfirm={confirmToggleStatus}
        title="Conferma cambio stato"
        subtitle={`Stai per ${productToToggleAttivo ? 'disattivare' : 'attivare'} il prodotto.`}
        message={`Sei sicuro di voler cambiare lo stato di "${productToToggle?.nomeProdotto}" in ${productToToggleAttivo ? 'NON ATTIVO' : 'ATTIVO'}?`}
        confirmText="CAMBIA STATO"
        confirmColor={productToToggleAttivo ? "#f44336" : "#4caf50"}
        icon={productToToggleAttivo ? <ErrorOutlineIcon sx={{ color: "white", fontSize: 50 }} /> : <CheckCircleOutlineIcon sx={{ color: "white", fontSize: 50 }} />}
      />
    </Box>
  );
};

export default GestioneProdotti;
